import {
	Component,
	OnDestroy,
	OnInit,
	ViewChild,
	ChangeDetectorRef,
	ElementRef
} from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	FormControl
} from '@angular/forms';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { GlobalsService } from '../../services/globals.service';
import { OrdersService } from '../../services/orders.service';
import { ReportsService } from '../../services/reports.service';
import { InventoryService } from '../../services/inventory.service';
import { PrintService } from '../../services/print.service';
import { CustomerService } from 'app/services/customer.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { startWith, map, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { Location } from '@angular/common';

@Component({
	selector: 'app-customer-export',
	templateUrl: './customer-export.component.html',
	styleUrls: ['./customer-export.component.scss']
})
export class CustomerExportComponent implements OnInit, OnDestroy {
	@ViewChild('print_table') printtable: ElementRef;
	@ViewChild('fromPicker') fromPicker: any;
	@ViewChild('toPicker') toPicker: any;

	@ViewChild(MatPaginator) paginator!: MatPaginator;

	@ViewChild(MatSort) set matSort(ms: MatSort) {
		this.sort = ms;
		this.setDataSourceAttributes();
	}

	color = 'blue';
	config: any = [];
	searchForm: UntypedFormGroup;

	cities: any[] = [];
	states: any[] = [];
	salespeople: any[] = [];
	productlines: any[] = [];
	clubs: any[] = [];
	paymentterms: any[] = [];

	selectedCities: any[] = [];
	selectedStates: any[] = [];
	selectedSalespeople: any[] = [];
	selectedProductLines: any[] = [];

	cityCtrl = new FormControl('');
	stateCtrl = new FormControl('');
	salespersonCtrl = new FormControl('');
	prodlineCtrl = new FormControl('');

	filteredCities: Observable < any[] > ;
	filteredStates: Observable < any[] > ;
	filteredSalespeople: Observable < any[] > ;
	filteredProductLines: Observable < any[] > ;

	fromdate: Date;
	todate: Date;
	filename = 'Customer_export_';
	sending = false;
	orderResults: any;

	dataSource: MatTableDataSource < any > ;
	dataObs: Observable < any > ;
	items_per_page = [5, 10, 20, 100, 500, 1000];
	pagesizedefault = 20;

	showAdvanced = false;
	/** Logged-in user is a salesperson; filter is fixed to their code and UI is hidden. */
	isSalespersonUser = false;
	private lockedSalesmancode: string | null = null;
	private sort: MatSort;
	private destroy$ = new Subject < void > ();
	displayedRows: any[] = [];

	constructor(
		private fb: UntypedFormBuilder,
		private _cdr: ChangeDetectorRef,
		private inventoryService: InventoryService,
		private customerService: CustomerService,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService,
		private globalsService: GlobalsService,
		private reportsService: ReportsService,
		private location: Location
	) {}

	ngOnInit() {
		const today = new Date();
		this.fromdate = new Date(today.getFullYear(), today.getMonth(), 1);
		this.todate = today;

		this.searchForm = this.fb.group({
			fromDateCtrl: [this.fromdate],
			toDateCtrl: [this.todate],
			keywords: [''],
			city: [''],
			states: [''],
			salesperson: [''],
			prodline: [''],
			payterms: [''],
			clubs: [''],
			clientsince: [''],
			balancedue: ['0'],
			salesfloor: [''],
			emailflag: ['0']
		});

		this.globalSearchService.configsubscription
			.pipe(takeUntil(this.destroy$))
			.subscribe(c => (this.config = c));

		this.color = this.globalSearchService.getColor();

		this.globalSearchService.user
			.pipe(takeUntil(this.destroy$))
			.subscribe((u: any) => {
				const code =
					u?.user?.issalesman &&
					u?.user?.salesman != null &&
					String(u.user.salesman).trim() !== ''
						? String(u.user.salesman).trim()
						: null;
				const wasLocked = this.isSalespersonUser;
				this.isSalespersonUser = !!code;
				this.lockedSalesmancode = code;
				if (!code && wasLocked) {
					this.selectedSalespeople = [];
				} else if (code) {
					this.applyLockedSalesperson();
				}
			});

		this.reportsService.getSalesPeople()
			.pipe(takeUntil(this.destroy$))
			.subscribe((r: any) => {
				this.salespeople = r;
				this.applyLockedSalesperson();
			});

		this.inventoryService.getItemProductlines()
			.pipe(takeUntil(this.destroy$))
			.subscribe((r: any) => this.productlines = r);

		this.customerService.getClubs()
			.pipe(takeUntil(this.destroy$))
			.subscribe((r: any) => this.clubs = r);

		this.customerService.getCities()
			.pipe(takeUntil(this.destroy$))
			.subscribe((r: any) => this.cities = r);

		this.customerService.getStates()
			.pipe(takeUntil(this.destroy$))
			.subscribe((r: any) => this.states = r);

		this.globalsService.getTerms()
			.pipe(takeUntil(this.destroy$))
			.subscribe(r => this.paymentterms = r);

		this.filteredCities = this.cityCtrl.valueChanges.pipe(
			startWith(''),
			map(value => this.filterList(value, this.cities, 'city', this.selectedCities, 'city'))
		);

		this.filteredStates = this.stateCtrl.valueChanges.pipe(
			startWith(''),
			map(value => this.filterList(value, this.states, 'statecode', this.selectedStates, 'statecode'))
		);

		this.filteredSalespeople = this.salespersonCtrl.valueChanges.pipe(
			startWith(''),
			map(value => this.filterList(value, this.salespeople, 'salesmanname', this.selectedSalespeople, 'salesmancode'))
		);

		this.filteredProductLines = this.prodlineCtrl.valueChanges.pipe(
			startWith(''),
			map(value => this.filterList(value, this.productlines, 'line_field', this.selectedProductLines, 'line_field'))
		);
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	filterList(value: any, list: any[] = [], key: string, exclude: any[] = [], excludeKey: string): any[] {
		if (!Array.isArray(list)) {return [];}
		const val = typeof value === 'string' ? value.toLowerCase() : '';

		return list.filter(item => {
			const field = item?.[key];
			const excluded = exclude.find(e => e?.[excludeKey] === item?.[excludeKey]);
			return typeof field === 'string' && field.toLowerCase().includes(val) && !excluded;
		});
	}

	selectValue(list: any[], value: any, key: string, displayKey ? : string) {
		if (!list.find(v => v?.[key] === value?.[key])) {
			list.push(displayKey ? {
				[key]: value[key], [displayKey]: value[displayKey] } : value);
		}
	}

	removeValue(list: any[], value: any, key ? : string): any[] {
		return key ? list.filter(v => v[key] !== value[key]) : list.filter(v => v !== value);
	}

	// Select Handlers
	selectCity(city: any) {
		this.selectValue(this.selectedCities, city, 'city');
		this.cityCtrl.setValue('');
	}

	selectState(state: any) {
		this.selectValue(this.selectedStates, state, 'statecode');
		this.stateCtrl.setValue('');
	}

	selectSalesperson(sp: any) {
		this.selectValue(this.selectedSalespeople, sp, 'salesmancode', 'salesmanname');
		this.salespersonCtrl.setValue('');
	}

	selectProductLine(line: any) {
		this.selectValue(this.selectedProductLines, line, 'line_field', 'line_description');
		this.prodlineCtrl.setValue('');
	}

	// Clear Handlers
	clearCities() {
		this.selectedCities = [];
		this.cityCtrl.setValue('');
	}

	clearStates() {
		this.selectedStates = [];
		this.stateCtrl.setValue('');
	}

	clearSalespeople() {
		if (this.isSalespersonUser) {
			this.applyLockedSalesperson();
			return;
		}
		this.selectedSalespeople = [];
		this.salespersonCtrl.setValue('');
	}

	clearProductLines() {
		this.selectedProductLines = [];
		this.prodlineCtrl.setValue('');
	}

	clearAllFilters() {
		this.clearCities();
		this.clearStates();
		this.clearSalespeople();
		this.clearProductLines();
		this.searchForm.reset();
	}

	private applyLockedSalesperson(): void {
		if (!this.lockedSalesmancode) {
			return;
		}
		const code = this.lockedSalesmancode;
		const match = (this.salespeople || []).find(
			(sp: any) => String(sp?.salesmancode) === code
		);
		this.selectedSalespeople = [
			match || { salesmancode: code, salesmanname: code }
		];
		this.salespersonCtrl.setValue('');
	}

	hasSelections(): boolean {
		return (
			this.selectedCities.length > 0 ||
			this.selectedStates.length > 0 ||
			this.selectedSalespeople.length > 0 ||
			this.selectedProductLines.length > 0
		);
	}

	getCustExport() {

		this.sending = true;

		const formValue = this.searchForm.value;

		const payload = {
			...formValue,
			cities: this.selectedCities.map(c => c.city),
			states: this.selectedStates.map(s => s.statecode),
			salespeople: this.selectedSalespeople.map(sp => sp.salesmancode),
			productlines: this.selectedProductLines.map(pl => pl.line_field)
		};

		this.customerService.getCustomerExport(payload).subscribe(r => {
			this.sending = false;
			this.orderResults = r;
			this.setPagination(r?.values || []);
		});
	}


	exportPdf() {
	  const today = new Date();
	  const encoded = this.globalSearchService.base64encode(this.generateExportHtml());
	  const data = {
			content: encoded,
			filename: `${this.filename}${today.getMonth()}${today.getDate()}${today.getFullYear()}`,
			title: 'Customer Export',
			subtitle: 'created ' + today.toLocaleString(),
			landscape: true,
	  };
	  this.printService.pdf(data).subscribe((r: any) =>
			this.globalSearchService.downloadPdf(r.content, data.filename)
	  );
	}

	exportXls() {
	  const today = new Date();
	  const encoded = this.globalSearchService.base64encode(this.generateExportHtml());
	  const data = {
			content: encoded,
			filename: `${this.filename}${today.getMonth()}${today.getDate()}${today.getFullYear()}`,
			title: 'Customer Export',
			subtitle: 'created ' + today.toLocaleString(),

	  };
	  this.printService.xls(data).subscribe((r: any) =>
			this.globalSearchService.downloadXls(r.content, data.filename)
	  );
	}


	generateExportHtml(): string {
	  const headerRow = `
		<tr>
		  <th>Number</th>
		  <th>Type</th>
		  <th>Customer</th>
		  <th>Branch</th>
		  <th>Address</th>
		  <th>Address 2</th>
		  <th>City</th>
          <th>Region</th>
		  <th>Zip</th>
		  <th>Contact</th>
		  <th>Type</th>
		  <th>Salesperson</th>
		  <th>Tax Group ID</th>
		  <th>Tax Ref</th>
		  <th>Phone</th>
		  <th>Email</th>
		  <th>Ship Method</th>
		  <th>Current</th>
		  <th>Over 30</th>
		  <th>Over 60</th>
		  <th>Over 90</th>
		  <th>Club</th>
		  <th>Date</th>
		  <th>Total Sales</th>
		  <th>Payment Terms</th>
		  <th>Notes</th>
		  <th>A/R Interest Rate</th>
		  <th>Username</th>
		  <th>Groups</th>
		  <th>Salon Alliance Id</th>
		</tr>`;

	  const bodyRows = this.orderResults.values.map(o => `
		<tr>
		  <td>${o.debtorno}.${o.branchcode}</td>
		  <td>${o.typename}</td>
		  <td>${o.name}</td>
		  <td>${o.brname}</td>
		  <td>${o.billing_address1}</td>
		  <td>${o.billing_address2}</td>
		  <td>${o.billing_address3}</td>
		  <td>${o.billing_address4}</td>
		  <td>${o.billing_address5}</td>
		  <td>${o.contactname}</td>
		  <td>${o.typename}</td>
		  <td>${o.salesmancode}</td>
		  <td>${o.taxgroupid}</td>
		  <td>${o.taxref}</td>
		  <td>${o.phoneno}</td>
		  <td>${o.email}</td>
		  <td>${o.shipping_method}</td>
		  <td style="text-align:right;">${this.formatCurrency(o.current)}</td>
		  <td style="text-align:right;">${this.formatCurrency(o.due)}</td>
		  <td style="text-align:right;">${this.formatCurrency(o.overdue1)}</td>
		  <td style="text-align:right;">${this.formatCurrency(o.overdue2)}</td>
		  <td>${o.club ? o.club : ''}</td>
		  <td>${this.formatDate(o.clientsince)}</td>
		  <td style="text-align:right;">${this.formatCurrency(o.total)}</td>
		  <th>${o.paymentterms}</th>
		  <th>${o.notes ? o.notes : ''}</th>
		  <th>${o.arintrate}</th>
		  <th>${o.username ? o.username: ''}</th>
		  <th>${o.group ? o.group: ''}</th>
		  <th>${o.salonallianceid ? o.salonallianceid : ''}</th>
		</tr>`).join('');

	  return `
		<div class="table-responsive">
		  <table class="table table-bordered table-striped table-hover table-sm">
			<thead class="thead-dark text-nowrap">${headerRow}</thead>
			<tbody>${bodyRows}</tbody>
		  </table>
		</div>`;
	}

	formatCurrency(val: any): string {
	  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
	}

	formatDate(val: any): string {
	  const date = new Date(val);
	  return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
	}

	setDataSourceAttributes() {
		if (this.paginator) {
			this.dataSource.paginator = this.paginator;
		}
	}


	setPagination(data: any[]) {
		this.dataSource = new MatTableDataSource<any>(data); // for filter/sort if needed
		this.dataObs = this.dataSource.connect();

		this._cdr.detectChanges();

		// Delay to ensure paginator is bound
		setTimeout(() => {
			this.updateDisplayedRows();
		}, 0);
	}


	updateDisplayedRows() {
		if (!this.paginator) {
			return;
		}

		if (!this.orderResults?.values || !Array.isArray(this.orderResults.values)) {
			return;
		}

		const start = this.paginator.pageIndex * this.paginator.pageSize;
		const end = start + this.paginator.pageSize;

		this.displayedRows = this.orderResults.values.slice(start, end);
	}

	back() {
		this.location.back();
	}

	ngAfterViewInit() {
		this.setDataSourceAttributes();
		this.updateDisplayedRows();
	}

}